import React, { useMemo } from 'react'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import FlashHotelRateOptionVariant from '../FlashHotelRateOptionVariant'
import Group from 'components/utils/Group'
import Pane from 'components/Common/Pane'
import BodyText from 'LuxKit/Typography/BodyText'
import Plural from 'components/utils/Formatters/Plural'
import { getDynamicPricingUrgencyLabelData } from 'components/OfferPage/utils'
import { useAppSelector } from 'hooks/reduxHooks'
import { getOptimizelyExperimentVariation } from 'lib/optimizely/optimizelyUtils'
import { OptimizelyExperiments } from 'constants/optimizely'
import { useUrgentOfferRoomDateData } from 'hooks/Offers/useUrgentOfferRoomDateData'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { useInView } from 'react-intersection-observer'
import config from 'constants/config'

interface Props {
  offer: App.Offer
  airports: Array<App.Airport>
  packageViews: Array<App.OfferPackageOptionView>
  room?: App.PackageRoomType
}

function FlashHotelPackageOptionsModalTab(props: Props) {
  const { offer, airports, packageViews, room } = props

  const destinationAirport = useMemo(() => airports.find(({ code }) => code === offer.flightDestinationPort), [airports, offer.flightDestinationPort])

  const cachedRoomDateData = useUrgentOfferRoomDateData(offer.id, room?.id)
  const liveRoomsBeforeSurcharge = useMemo(() => getDynamicPricingUrgencyLabelData(cachedRoomDateData, packageViews[0].availableRooms), [cachedRoomDateData, packageViews])
  const urgencyTagAbTestVariation = useAppSelector(state => getOptimizelyExperimentVariation(state, OptimizelyExperiments.lereDynamicPricingUrgencyTag))
  const [sectionRef, sectionInView] = useInView({ triggerOnce: false, threshold: 1 })
  const canTriggerExperiment = useMemo(() => {
    return sectionInView && !!liveRoomsBeforeSurcharge && config.BRAND === 'luxuryescapes'
  }, [sectionInView, liveRoomsBeforeSurcharge])
  useOptimizelyExperiment(OptimizelyExperiments.lereDynamicPricingUrgencyTag, canTriggerExperiment)

  const experimentRoomsBeforeSurcharge = useMemo(() => {
    if (urgencyTagAbTestVariation === 'on') {
      return liveRoomsBeforeSurcharge
    }
    return undefined
  }, [urgencyTagAbTestVariation, liveRoomsBeforeSurcharge])

  return (
    <Pane ref={sectionRef} background="neutral-six">
      {packageViews.length > 1 && <Pane background="neutral-eight" padding="12 20">
        <BodyText variant="small" colour="neutral-three"><Plural singular="package option" count={packageViews.length} withCount /> available</BodyText>
      </Pane>}
      <Group direction="vertical" gap={8}>
        {packageViews.map((view, index) => <Pane key={view.package.uniqueKey}>
          <FlashHotelRateOptionVariant
            offer={offer}
            roomsBeforeSurcharge={experimentRoomsBeforeSurcharge}
            optionNumber={index + 1}
            packageView={view}
            flightDestination={destinationAirport}
            showOptionLabel={packageViews.length > 1}
          />
        </Pane>)}
      </Group>
    </Pane>
  )
}

export default withOfferPageAndReduxState((state, appState) => {
  return {
    offer: state.offer,
    airports: appState.geo.airports,
    room: state.room,
  }
}, FlashHotelPackageOptionsModalTab)
